/**
 * Portal statement (tracker item 45): the customer's account activity with a
 * running balance, in cards a phone can read, with "Load more".
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PortalStatement, PortalStatementLine } from '../../api/types';
import { usePortal, usePortalApi } from './PortalContext';
import { EmptyBlock, FailureBlock, LoadingBlock } from './PortalStates';
import { invoiceDate, invoiceMoney } from '../invoiceUi';
import { describePortalFailure, PortalFailure } from '../../utils/portal';
import { secondaryButtonClass } from '../../components/formStyles';

const PAGE_SIZE = 50;

export function PortalStatementPage() {
  const api = usePortalApi();
  const { reportFailure } = usePortal();
  const [head, setHead] = useState<PortalStatement | null>(null);
  const [lines, setLines] = useState<PortalStatementLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [failure, setFailure] = useState<PortalFailure | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async (pageNo: number) => {
    const id = ++requestId.current;
    if (pageNo === 1) setLoading(true); else setMoreLoading(true);
    setFailure(null);
    try {
      const result = await api.getPortalStatement({ page: pageNo, limit: PAGE_SIZE });
      if (id !== requestId.current) return;
      setHead(result);
      setLines((current) => (pageNo === 1 ? result.lines : [...current, ...result.lines]));
    } catch (error) {
      if (id !== requestId.current) return;
      const described = describePortalFailure(error, 'We could not load your statement.');
      setFailure(described);
      reportFailure(described);
    } finally {
      if (id === requestId.current) { setLoading(false); setMoreLoading(false); }
    }
  }, [api, reportFailure]);

  useEffect(() => { void load(1); }, [load]);

  const hasMore = !!head && head.pagination.page < head.pagination.pages;
  return (
    <div className="space-y-4">
      <h1 className="text-lg font-bold text-gray-900">Statement</h1>
      {loading && <LoadingBlock label="Loading statement" />}
      {failure && <FailureBlock failure={failure} onRetry={() => void load(head && lines.length ? head.pagination.page + 1 : 1)} />}
      {head && !loading && (
        <>
          <section className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-sm text-gray-600">{head.customerName} · as of {invoiceDate(head.statementDate)}</p>
            <dl className="mt-2 grid grid-cols-[1fr_auto] gap-y-1 text-sm">
              <dt className="text-gray-600">Opening balance</dt><dd>{invoiceMoney(head.openingBalance)}</dd>
              <dt className="font-semibold">Balance</dt><dd className="font-bold" data-testid="statement-closing">{invoiceMoney(head.closingBalance)}</dd>
            </dl>
          </section>
          {lines.length === 0 ? (
            <EmptyBlock>There is no activity on your account yet.</EmptyBlock>
          ) : (
            <ul className="space-y-2" aria-label="Statement lines">
              {lines.map((line, index) => (
                <li key={index} className="rounded-lg bg-white p-3 text-sm shadow-sm ring-1 ring-gray-200" data-testid="statement-line">
                  <div className="flex justify-between gap-3">
                    <span className="min-w-0 break-words font-medium text-gray-900">{line.description}</span>
                    <span className="whitespace-nowrap font-semibold">
                      {Number(line.charge) > 0 ? invoiceMoney(line.charge) : `−${invoiceMoney(line.credit)}`}
                    </span>
                  </div>
                  <div className="mt-0.5 flex justify-between text-xs text-gray-500">
                    <span>{invoiceDate(line.date)}{line.reference ? ` · ${line.reference}` : ''}</span>
                    <span>Balance {invoiceMoney(line.runningBalance)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {hasMore && !failure && (
            <button type="button" className={`${secondaryButtonClass} w-full`} disabled={moreLoading} onClick={() => void load(head.pagination.page + 1)}>
              {moreLoading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </>
      )}
      <Link to="/portal/invoices" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline">&larr; Invoices</Link>
    </div>
  );
}
